import React, { useEffect, useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import api from '../../config/axios';
import { date, time } from '../../utils/date';
import { useStateValue } from '../../store';

const FRANQUICIA_SC = '5e95f51f-5b66-4c58-b2af-7a54e8b1df1e';

const DetalleEnvio = () => {
    const { id } = useParams();
    const [{ auth }] = useStateValue();
    const [envio, setEnvio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [modalAsignar, setModalAsignar] = useState(false);
    const [userIdSeleccionado, setUserIdSeleccionado] = useState('');
    const [procesando, setProcesando] = useState(false);

    const franchiseId = (auth.user && auth.user.franchiseId) || FRANQUICIA_SC;
    const isAdmin =
        auth.user && (auth.user.role === 'ADMIN' || auth.user.role === 'FRANCHISE');

    useEffect(() => {
        fetchEnvio();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Inicializar el mapa con la ruta: negocio (origen) → receptor (destino)
    useEffect(() => {
        if (!envio || loading) return;

        const mapDiv = document.getElementById('map-envio');
        if (!mapDiv || !window.google) return;

        const receptorLat = envio.receiverLatitude
            ? parseFloat(envio.receiverLatitude)
            : null;
        const receptorLng = envio.receiverLongitude
            ? parseFloat(envio.receiverLongitude)
            : null;

        if (!receptorLat || !receptorLng) return;

        const destino = { lat: receptorLat, lng: receptorLng };

        const negocioLat =
            envio.business && envio.business.addressLat
                ? parseFloat(envio.business.addressLat)
                : null;
        const negocioLng =
            envio.business && envio.business.addressLng
                ? parseFloat(envio.business.addressLng)
                : null;

        // eslint-disable-next-line no-undef
        const map = new google.maps.Map(mapDiv, {
            zoom: 14,
            mapTypeControl: false,
            streetViewControl: false,
        });

        // eslint-disable-next-line no-undef
        const bounds = new google.maps.LatLngBounds();

        // Marker del receptor (destino) — rojo
        // eslint-disable-next-line no-undef
        const markerDestino = new google.maps.Marker({
            position: destino,
            map,
            label: 'B',
        });
        bounds.extend(destino);

        if (negocioLat && negocioLng) {
            const origen = { lat: negocioLat, lng: negocioLng };

            // Marker del negocio (origen) — verde
            // eslint-disable-next-line no-undef
            const markerOrigen = new google.maps.Marker({
                position: origen,
                map,
                label: 'A',
            });
            bounds.extend(origen);

            // Línea directa entre origen y destino
            // eslint-disable-next-line no-undef
            new google.maps.Polyline({
                path: [origen, destino],
                map,
                strokeColor: '#00a4d3',
                strokeWeight: 3,
                strokeOpacity: 0.8,
                geodesic: true,
            });

            map.fitBounds(bounds);
            // Evitar zoom excesivo cuando los puntos están muy cerca
            setTimeout(() => {
                if (map.getZoom() > 16) map.setZoom(16);
            }, 100);
        } else {
            // Sin GPS del negocio: solo el marker del receptor
            map.setCenter(destino);
            map.setZoom(16);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [envio, loading]);

    const fetchEnvio = async () => {
        try {
            const { data } = await api.get(`/deliveryOrders/${id}`);
            setEnvio(data.data);
        } catch (err) {
            toast.error('Error al cargar el envío');
        } finally {
            setLoading(false);
        }
    };

    const abrirModalAsignar = async () => {
        if (users.length === 0) {
            try {
                const { data } = await api.get(`/franchise/${franchiseId}/users`);
                const repartidores = (data.data || []).filter(
                    (u) => u.role === 'DELIVERY' || u.role === 'FRANCHISE',
                );
                setUsers(repartidores);
            } catch (err) {
                toast.error('Error al cargar los repartidores');
                return;
            }
        }
        setModalAsignar(true);
    };

    const asignar = async () => {
        if (!userIdSeleccionado) {
            toast.warning('Selecciona un repartidor');
            return;
        }
        setProcesando(true);
        try {
            await api.post(`/deliveryOrders/${id}/asignar`, {
                userId: userIdSeleccionado,
            });
            toast.success('Envío asignado');
            setModalAsignar(false);
            setUserIdSeleccionado('');
            await fetchEnvio();
        } catch (err) {
            toast.error('Error al asignar el envío');
        } finally {
            setProcesando(false);
        }
    };

    const marcarTransaccion = async (name) => {
        setProcesando(true);
        try {
            await api.post(`/deliveryOrders/${id}/transaction`, { name });
            toast.success(`Estado actualizado: ${name}`);
            await fetchEnvio();
        } catch (err) {
            toast.error('Error al actualizar el estado');
        } finally {
            setProcesando(false);
        }
    };

    // Estado operativo: última transacción de ciclo de vida,
    // ignorando 'Pago Confirmado' (no cambia el estado operativo)
    const ultimaTransaccion = () => {
        if (!envio.transactions || envio.transactions.length === 0) return null;
        const ciclo = envio.transactions.filter(
            (t) => t.name !== 'Pago Confirmado',
        );
        const ordenadas = (ciclo.length ? ciclo : envio.transactions).sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
        return ordenadas[ordenadas.length - 1];
    };

    const estaPagado = () =>
        (envio.transactions || []).some((t) => t.name === 'Pago Confirmado');

    // Badge de estado — mismo patrón que order-detail/renderBadge
    const renderEstadoBadge = (status) => {
        switch (status) {
            case 'Pedido Creado':
                return (
                    <span
                        style={{ fontSize: 16, height: 25 }}
                        className="badge badge-warning"
                    >
                        Creado
                    </span>
                );
            case 'Pedido Enviado':
                return (
                    <span
                        style={{ fontSize: 16, height: 25 }}
                        className="badge badge-info"
                    >
                        Enviado
                    </span>
                );
            case 'Pedido Entregado':
                return (
                    <span
                        style={{ fontSize: 16, height: 25 }}
                        className="badge badge-success"
                    >
                        Entregado
                    </span>
                );
            case 'Pedido Cancelado':
                return (
                    <span
                        style={{ fontSize: 16, height: 25 }}
                        className="badge badge-danger"
                    >
                        Cancelado
                    </span>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <Fragment>
                <div className="container-fluid text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                </div>
            </Fragment>
        );
    }

    if (!envio) {
        return (
            <Fragment>
                <div className="container-fluid text-center py-5">
                    <p style={{ fontSize: 18, color: '#777' }}>
                        Envío no encontrado
                    </p>
                </div>
            </Fragment>
        );
    }

    const estado = ultimaTransaccion();

    // URL de WhatsApp para el receptor — mismo patrón que order-detail
    const phoneParsed = envio.receiverPhone;
    const whatsappUrl = () =>
        `https://wa.me/${phoneParsed}?text=Hola, soy ${
            auth.user ? auth.user.name.split(' ')[0] : 'Aguaman'
        } de Aguaman. Le confirmo su envío.`;

    const destination =
        envio.receiverLatitude && envio.receiverLongitude
            ? `${envio.receiverLatitude},${envio.receiverLongitude}`
            : `${envio.receiverStreet1}, ${envio.receiverStreet2 || ''}`;

    const negocio =
        envio.business && envio.business.name
            ? envio.business.name
            : envio.businessName || 'Sin origen';

    // Nombre formateado del repartidor (primera palabra + inicial del apellido)
    const nombreRepartidor = envio.user
        ? `${envio.user.name.split(' ')[0]}${
              envio.user.name.split(' ')[1]
                  ? ' ' + envio.user.name.split(' ')[1].substring(0, 1) + '.'
                  : ''
          }`
        : null;

    return (
        <Fragment>
            <div className="container-fluid">
                <div className="row">
                    {/* Columna principal — info del receptor + ruta en mapa */}
                    <div className="col-sm-12 col-lg-8">
                        {/* Repartidor asignado — mismo estilo que order-detail */}
                        {envio.user && (
                            <div className="card mb-3">
                                <div className="card-body py-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex justify-content-start align-items-center">
                                            <img
                                                src={
                                                    envio.user.profile_img ||
                                                    'https://d25vjkdnn83buj.cloudfront.net/man.5896d3f9.jpeg'
                                                }
                                                height={40}
                                                width={40}
                                                style={{ borderRadius: 40 }}
                                                alt="repartidor"
                                            />
                                            &nbsp;&nbsp;
                                            <p
                                                style={{
                                                    color: '#777',
                                                    fontSize: 14,
                                                    margin: 0,
                                                }}
                                            >
                                                {nombreRepartidor}
                                            </p>
                                        </div>
                                        {isAdmin && (
                                            <button
                                                className="btn btn-xs"
                                                onClick={abrirModalAsignar}
                                                title="Reasignar repartidor"
                                            >
                                                <i className="fa fa-pencil"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Acciones principales: Enviar / Entregar */}
                        {estado &&
                            estado.name !== 'Pedido Cancelado' &&
                            estado.name !== 'Pedido Entregado' && (
                                <div className="d-flex justify-content-center mb-3">
                                    {estado.name === 'Pedido Creado' && (
                                        <button
                                            className="btn btn-info btn-lg mx-2"
                                            onClick={() =>
                                                marcarTransaccion('Pedido Enviado')
                                            }
                                            disabled={procesando}
                                        >
                                            <i className="fa fa-truck"></i>{' '}
                                            Enviar Pedido
                                        </button>
                                    )}
                                    {estado.name === 'Pedido Enviado' && (
                                        <button
                                            className="btn btn-success btn-lg mx-2"
                                            onClick={() =>
                                                marcarTransaccion('Pedido Entregado')
                                            }
                                            disabled={procesando}
                                        >
                                            <i className="fa fa-check-circle"></i>{' '}
                                            Pedido Entregado
                                        </button>
                                    )}
                                </div>
                            )}

                        <div className="card">
                            <div className="card-body">
                                {/* Estado + asignar repartidor — arriba del Origen→Destino */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center">
                                        {estado && renderEstadoBadge(estado.name)}
                                        {estaPagado() && (
                                            <span
                                                className="badge badge-success ml-2"
                                                style={{ fontSize: 14 }}
                                            >
                                                Pago confirmado ✓
                                            </span>
                                        )}
                                        {!estado && !estaPagado() && (
                                            <span
                                                className="badge badge-secondary"
                                                style={{ fontSize: 14 }}
                                            >
                                                Sin estado
                                            </span>
                                        )}
                                    </div>
                                    {!envio.user && (
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={abrirModalAsignar}
                                            disabled={procesando}
                                        >
                                            <i className="fa fa-user-plus"></i> Asignar
                                            repartidor
                                        </button>
                                    )}
                                </div>
                                {/* Componente visual Origen → Destino (vertical, línea a la izquierda) */}
                                <div style={{ display: 'flex', marginBottom: 16 }}>
                                    {/* Línea conectora a la izquierda */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 12 }}>
                                        <div style={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #00a4d3', marginTop: 4 }} />
                                        <div style={{ width: 2, flex: 1, minHeight: 28, borderLeft: '2px dashed #ccc' }} />
                                        <i className="fa fa-map-marker" style={{ color: '#e83e8c', fontSize: 20, marginBottom: 2 }} />
                                    </div>
                                    {/* Contenido: origen arriba, destino abajo */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontWeight: 'bold', margin: 0, fontSize: 16 }}>
                                            {negocio}
                                        </p>
                                        <p style={{ color: '#999', margin: '2px 0 0', fontSize: 13 }}>
                                            {envio.business && envio.business.addressStreet1
                                                ? `${envio.business.addressStreet1}${envio.business.addressStreet2 ? ` — ${envio.business.addressStreet2}` : ''}`
                                                : 'Dirección no registrada'}
                                        </p>
                                        <div style={{ height: 12 }} />
                                        <p style={{ fontWeight: 'bold', margin: 0, fontSize: 16 }}>
                                            {envio.receiverName}
                                        </p>
                                        <p style={{ color: '#999', margin: '2px 0 0', fontSize: 13 }}>
                                            {envio.receiverStreet1}
                                            {envio.receiverStreet2
                                                ? ` — ${envio.receiverStreet2}`
                                                : ''}
                                        </p>
                                    </div>
                                </div>

                                {/* Teléfono del receptor con botones llamar + WhatsApp */}
                                <div className="d-flex align-items-center mt-2 mb-3">
                                    <a
                                        href={`tel:+${phoneParsed}`}
                                        className="btn btn-info btn-lg mr-2"
                                    >
                                        <i className="fa fa-phone"></i> Llamar
                                    </a>
                                    <a
                                        href={whatsappUrl()}
                                        target="_blank"
                                        className="btn btn-success btn-lg btn-whatsapp"
                                    >
                                        <i className="fa fa-whatsapp"></i> WhatsApp
                                    </a>
                                </div>
                                {/* Ruta en Google Maps: negocio → receptor */}
                                {envio.receiverLatitude &&
                                    envio.receiverLongitude && (
                                        <Fragment>
                                            <div
                                                id="map-envio"
                                                style={{
                                                    width: '100%',
                                                    height: 300,
                                                }}
                                            />
                                            <div className="d-flex justify-content-center mt-1">
                                                <a
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${destination}`}
                                                    className="btn btn-sm btn-outline-secondary mt-2"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Abrir ruta en Google Maps
                                                </a>
                                            </div>
                                        </Fragment>
                                    )}
                            </div>
                        </div>

                        {/* Cobro en entrega — card propia, texto más oscuro */}
                        {envio.tipoCobro && (
                            <div className="card mt-3">
                                <div className="card-body">
                                    <div
                                        className="alert mb-0"
                                        style={{
                                            backgroundColor:
                                                envio.tipoCobro === 'pagado'
                                                    ? '#d4edda'
                                                    : envio.tipoCobro ===
                                                      'completo'
                                                    ? '#fff3cd'
                                                    : '#d1ecf1',
                                            border: '1px solid transparent',
                                            borderRadius: 6,
                                            padding: '12px 16px',
                                        }}
                                    >
                                        <strong>Cobrar en entrega:</strong>{' '}
                                        {envio.tipoCobro === 'completo' && (
                                            <span>
                                                ${' '}
                                                {(
                                                    Number(envio.valorPaquete || 0) +
                                                    Number(envio.tarifaEnvio)
                                                ).toLocaleString('es-CO')}{' '}
                                                COP
                                                <br />
                                                <small style={{ color: '#555' }}>
                                                    (Paquete ${' '}
                                                    {Number(
                                                        envio.valorPaquete || 0,
                                                    ).toLocaleString('es-CO')}
                                                    {' '}+ Envío ${' '}
                                                    {Number(
                                                        envio.tarifaEnvio,
                                                    ).toLocaleString('es-CO')})
                                                </small>
                                            </span>
                                        )}
                                        {envio.tipoCobro === 'solo_envio' && (
                                            <span>
                                                ${' '}
                                                {Number(
                                                    envio.tarifaEnvio,
                                                ).toLocaleString('es-CO')}{' '}
                                                COP
                                                <br />
                                                <small style={{ color: '#555' }}>
                                                    Solo el envío — el paquete
                                                    ya está pagado
                                                </small>
                                            </span>
                                        )}
                                        {envio.tipoCobro === 'pagado' && (
                                            <span>
                                                ✓ No cobrar — todo ya pagado
                                            </span>
                                        )}
                                        {envio.vueltos != null &&
                                            Number(envio.vueltos) > 0 && (
                                                <div
                                                    style={{
                                                        marginTop: 8,
                                                        fontSize: 15,
                                                        fontWeight: 'bold',
                                                        color: '#333',
                                                    }}
                                                >
                                                    💵 Vueltos para ${' '}
                                                    {Number(
                                                        envio.vueltos,
                                                    ).toLocaleString('es-CO')}{' '}
                                                    COP
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Detalle del envío */}
                        <div className="card mt-3">
                            <div className="card-body">
                                <dl className="row mb-0">
                                    <dt className="col-sm-4">Tarifa</dt>
                                    <dd className="col-sm-8">
                                        $ {Number(envio.tarifaEnvio).toLocaleString('es-CO')}{' '}
                                        COP
                                    </dd>

                                    <dt className="col-sm-4">Forma de pago</dt>
                                    <dd className="col-sm-8">
                                        {envio.paymentMethod
                                            ? envio.paymentMethod.name
                                            : '-'}
                                    </dd>

                                    {envio.descripcionPaquete && (
                                        <Fragment>
                                            <dt className="col-sm-4">Paquete</dt>
                                            <dd className="col-sm-8">
                                                {envio.descripcionPaquete}
                                            </dd>
                                        </Fragment>
                                    )}

                                    <dt className="col-sm-4">Creado</dt>
                                    <dd className="col-sm-8">
                                        {date(envio.createdAt)} {time(envio.createdAt)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Columna lateral — bitácora */}
                    <div className="col-sm-12 col-lg-4">
                        <div className="card">
                            <div className="card-body">
                                <div className="clearfix"></div>
                                <h4>Bitácora</h4>
                                <hr />
                                {(envio.transactions || [])
                                    .slice()
                                    .sort(
                                        (a, b) =>
                                            new Date(a.createdAt) -
                                            new Date(b.createdAt),
                                    )
                                    .map((item, index) => (
                                        <div key={`bitacora-${index}`}>
                                            <div className="d-flex justify-content-between mb-3">
                                                <div>
                                                    <p>
                                                        <strong>
                                                            {item.name.split(' ')[1]}
                                                        </strong>
                                                    </p>
                                                    {item.observation && (
                                                        <p
                                                            className="text-muted"
                                                            style={{ fontSize: 13 }}
                                                        >
                                                            {item.observation}
                                                        </p>
                                                    )}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <p>
                                                        {date(item.createdAt).split('-')[0]}/
                                                        {date(item.createdAt).split('-')[1]}{' '}
                                                        {time(item.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cancelar Pedido — parte inferior */}
                {estado && estado.name === 'Pedido Creado' && (
                    <div className="d-flex justify-content-center mt-3 mb-3">
                        <button
                            className="btn btn-danger btn-lg"
                            onClick={() =>
                                marcarTransaccion('Pedido Cancelado')
                            }
                            disabled={procesando}
                        >
                            <i className="fa fa-times-circle"></i> Cancelar
                            Pedido
                        </button>
                    </div>
                )}
            </div>

            {/* Modal asignar repartidor */}
            {modalAsignar && (
                <div
                    className="modal fade show"
                    style={{
                        display: 'block',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Asignar repartidor</h5>
                                <button
                                    type="button"
                                    className="close"
                                    onClick={() => setModalAsignar(false)}
                                >
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Repartidor</label>
                                    <select
                                        className="form-control"
                                        value={userIdSeleccionado}
                                        onChange={(e) =>
                                            setUserIdSeleccionado(e.target.value)
                                        }
                                    >
                                        <option value="">Seleccionar...</option>
                                        {users.map((u) => (
                                            <option key={u.id} value={u.id}>
                                                {u.name} ({u.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setModalAsignar(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={asignar}
                                    disabled={procesando}
                                >
                                    {procesando ? 'Asignando...' : 'Asignar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default DetalleEnvio;